/**
 * AppVersionNotifier — luxury "new version available" toast.
 *
 * Mounted once at the root. When `appVersion.js` detects a fresh build it
 * fires `onVersionStale`. We:
 *   1. Show a sticky toast in the bottom-right with a "Refresh now" CTA.
 *   2. Start a 30 s countdown; if the user doesn't act, we auto-reload.
 *   3. Reload via `hardReload()` (cache-bust query) so the new build is
 *      definitely fetched fresh from the origin.
 *
 * The toast is intentionally calm — no aggressive redirect, matches the
 * Cartier/Sotheby's tone.
 */
import { useEffect, useState } from "react";
import { onVersionStale, hardReload } from "../lib/appVersion";
import TX from "./TX";

const AUTO_RELOAD_SECONDS = 30;

const AppVersionNotifier = () => {
  const [visible, setVisible] = useState(false);
  const [seconds, setSeconds] = useState(AUTO_RELOAD_SECONDS);

  useEffect(() => {
    const off = onVersionStale(() => setVisible(true));
    return () => off();
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (seconds <= 0) { hardReload(); return; }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [visible, seconds]);

  if (!visible) return null;

  return (
    <div
      data-testid="app-version-notifier"
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-[130] max-w-sm bg-noir border border-gold/40 shadow-2xl"
    >
      <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
      <div className="p-5">
        <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">
          <TX>Update Available</TX>
        </div>
        <div className="text-white text-sm font-light leading-relaxed mb-4">
          <TX>New website version is live. Refresh to load the latest experience.</TX>
        </div>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="app-version-dismiss"
            onClick={() => setVisible(false)}
            className="text-white/45 hover:text-white/80 transition-colors font-accent text-[10px] tracking-[0.28em] uppercase"
          >
            <TX>Later</TX>
          </button>
          <button
            type="button"
            data-testid="app-version-refresh"
            onClick={hardReload}
            className="bg-gold text-noir px-4 py-2 font-accent text-[10px] tracking-[0.28em] uppercase hover:bg-gold/90 transition-colors"
          >
            <TX>Refresh now</TX>
            <span className="ml-2 text-noir/60">({seconds}s)</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppVersionNotifier;
